import { useState, useEffect, useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { NotificationBell } from "./NotificationBell";
import { ScannerOverlay } from "../mobile/ScannerOverlay";
import { Drawer } from "../Drawer";
import { useIsMobile } from "../../lib/useIsMobile";
import { usePwaInstall } from "../../lib/pwa";
import { NAV, MOBILE_TABBAR } from "../../lib/nav";
import { TabBar } from "./TabBar";
import { TabViews } from "./TabViews";
import { useTabStore } from "../../lib/tabStore";
import "./AppShell.css";

export function AppShell() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  // Mobil (≤767px): off-canvas nav açık mı. Rota değişince + Escape ile kapanır.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false); // mobil barkod/QR tarayıcı
  const pwa = usePwaInstall(); // "Ana ekrana ekle" (yüklenebilir webapp)
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileNavOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);
  // Mobil pull-to-refresh: içerik en üstteyken aşağı çekince aktif sekmedeki sorgular yenilenir.
  const mContentRef = useRef<HTMLDivElement>(null);
  const pullY0 = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  function onPullDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (mContentRef.current && mContentRef.current.scrollTop <= 0) pullY0.current = e.clientY;
  }
  function onPullMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (pullY0.current == null || refreshing) return;
    const dy = e.clientY - pullY0.current;
    if (dy > 6 && (mContentRef.current?.scrollTop ?? 0) <= 0) setPull(Math.min(72, dy * 0.45));
    else if (dy <= 0) setPull(0);
  }
  function onPullEnd() {
    if (pull > 52 && !refreshing) {
      setRefreshing(true); setPull(48);
      Promise.resolve(qc.invalidateQueries()).finally(() => { setRefreshing(false); setPull(0); });
    } else setPull(0);
    pullY0.current = null;
  }
  // Sol menü daralt/genişlet (localStorage'da kalıcı). Daralınca ikonlar büyür, etiketler gizlenir, hover'da tooltip.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("steperp.sidebarCollapsed") === "1");
  const [navTip, setNavTip] = useState<{ label: string; top: number } | null>(null);
  const meQ = trpc.auth.me.useQuery();
  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: async () => {
      await qc.invalidateQueries();
      navigate("/login");
    },
  });

  const user = meQ.data?.user ?? null;
  // Sayfa erişimi: kullanıcının grubundaki sayfalar (süper admin hepsini görür).
  // me henüz yüklenmediyse menüyü boş bırakma — tümünü göster (PageGate zaten rotada korur).
  const allowedPages = (user as { pages?: string[] } | null)?.pages;
  const isSuper = !!(user as { isSuperAdmin?: boolean } | null)?.isSuperAdmin;
  const canSee = (key: string) => isSuper || !allowedPages || allowedPages.includes(key);
  const visibleNav = NAV
    .map((g) => ({ ...g, items: g.items.filter((i) => canSee(i.pageKey)) }))
    .filter((g) => g.items.length > 0);
  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() || user.email[0]!.toUpperCase()
    : "?";
  const fullName = user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email : "—";
  // Çok-tesisli: süper-admin (çok tesisli) için tesis değiştirici; seçim localStorage'a yazılır, header ile gider.
  const facilities = (user as { facilities?: Array<{ id: string; code: string; name: string }> } | null)?.facilities ?? [];
  const activeFacilityId = (user as { activeFacilityId?: string | null } | null)?.activeFacilityId ?? null;
  function switchFacility(id: string) {
    if (id) localStorage.setItem("steperp.activeFacility", id);
    else localStorage.removeItem("steperp.activeFacility");
    qc.invalidateQueries();
    // Açık sekmeler ESKİ tesisin seçimlerini taşır (seçili lokasyon/lot/merkez).
    // Sunucu verisini tazelemek yetmez — sekmeler sıfırlanmalı. Gerekçe tabStore'da.
    useTabStore.getState().resetForFacility();
  }
  /**
   * SERVICE WORKER BİLDİRİM YÖNLENDİRMESİ.
   * Eskiden `client.navigate()` ile TAM SAYFA yenileniyordu; çoklu sekmede bu, açık tüm
   * sekmelerin bellekteki durumunu silmek demekti. Artık SW mesaj gönderiyor, gezinmeyi
   * uygulama kendi router'ıyla yapıyor — sekmeler ve yarım işler korunuyor.
   */
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; path?: string } | null;
      if (d?.type === "steperp:navigate" && typeof d.path === "string") navigate(d.path);
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);

  function toggleSidebar() {
    setCollapsed((c) => { const n = !c; localStorage.setItem("steperp.sidebarCollapsed", n ? "1" : "0"); return n; });
    setNavTip(null);
  }
  // Daralt modunda: ikona gelince etiketi sağda fixed popup göster (kırpılmaz — overflow'un dışında).
  const onNavEnter = (label: string) => (e: ReactMouseEvent<HTMLElement>) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setNavTip({ label, top: r.top + r.height / 2 });
  };
  const onNavLeave = () => setNavTip(null);

  // "Uygulamayı Yükle" — TÜM kullanıcılar, masaüstü + mobil. Tarayıcı native diyalog sunuyorsa (canPrompt)
  // onu açar; sunmuyorsa (iOS Safari, Firefox, prompt henüz tetiklenmemiş) platforma uyarlı yönergeyi gösterir.
  async function handleInstall() {
    setMobileNavOpen(false);
    setMenuOpen(false);
    if (pwa.canPrompt) {
      const r = await pwa.promptInstall();
      if (r !== "accepted" && r !== "dismissed") setInstallHelpOpen(true); // native diyalog bir şekilde açılamadıysa yönerge
    } else {
      setInstallHelpOpen(true);
    }
  }
  // Kurulum yardımı — responsive Drawer (mobilde tam ekran, masaüstünde yan panel); her iki kabukta ortak render.
  const installHelp = (
    <Drawer open={installHelpOpen} onClose={() => setInstallHelpOpen(false)} size="sm" title="Uygulamayı Yükle" subtitle="STEPERP'i cihazına kur, tam ekran kullan">
      <div style={{ fontSize: 13.5, color: "var(--neutral-700)", lineHeight: 1.6, display: "flex", flexDirection: "column", gap: 16 }}>
        <p style={{ margin: 0 }}>STEPERP'i telefon ya da bilgisayarının ana ekranına ekleyip ayrı bir uygulama gibi tam ekran kullanabilirsin.</p>
        {pwa.isIOS ? (
          <div>
            <p style={{ margin: "0 0 8px", fontWeight: 600, color: "var(--neutral-900)" }}>iPhone / iPad (Safari)</p>
            <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 8 }}>
              <li>Alttaki <b>Paylaş</b> düğmesine dokun (yukarı ok <span className="mono">⬆︎</span>).</li>
              <li>Menüden <b>"Ana Ekrana Ekle"</b>yi seç.</li>
              <li>Sağ üstten <b>"Ekle"</b> ile onayla.</li>
            </ol>
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--neutral-500)" }}>Not: Bu adımlar yalnız <b>Safari</b> tarayıcısında çalışır.</p>
          </div>
        ) : (
          <>
            <div>
              <p style={{ margin: "0 0 8px", fontWeight: 600, color: "var(--neutral-900)" }}>Android (Chrome)</p>
              <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                <li>Sağ üstteki <b>⋮</b> menüsünü aç.</li>
                <li><b>"Uygulamayı yükle"</b> ya da <b>"Ana ekrana ekle"</b>yi seç.</li>
              </ol>
            </div>
            <div>
              <p style={{ margin: "0 0 8px", fontWeight: 600, color: "var(--neutral-900)" }}>Bilgisayar (Chrome / Edge)</p>
              <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                <li>Adres çubuğunun sağındaki <b>yükle simgesine</b> (monitör / ⊕) tıkla.</li>
                <li>Açılan pencerede <b>"Yükle"</b> ile onayla.</li>
              </ol>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );

  // ═══ MOBİL KABUK (≤767px) — GENTAŞ Saha referansı: navy topbar + sol çekmece + içerik ═══
  if (isMobile) {
    const allItems = NAV.flatMap((g) => g.items);
    const activeItem = allItems.find((i) => location.pathname === i.to || location.pathname.startsWith(i.to + "/"));
    const pageTitle = activeItem?.label ?? "STEPERP";
    const facSub = facilities.length > 1 && activeFacilityId ? facilities.find((f) => f.id === activeFacilityId)?.name : null;
    return (
      <div className="m-app">
        <header className="m-topbar">
          <button type="button" className="m-iconbtn" aria-label="Menü" onClick={() => setMobileNavOpen(true)}>
            <Icon name="list" size={20} />
          </button>
          <div className="m-title">{pageTitle}{facSub ? <small>{facSub}</small> : null}</div>
          <button type="button" className="m-iconbtn" aria-label="Barkod / QR tara" onClick={() => setScannerOpen(true)}>
            <Icon name="qr" size={19} />
          </button>
          <NotificationBell />
        </header>

        <div className="m-scrollwrap">
          <div
            className="m-content"
            ref={mContentRef}
            style={{
              transform: pull ? `translateY(${pull}px)` : undefined,
              transition: pullY0.current == null ? "transform 0.25s cubic-bezier(0.4,0,0.2,1)" : "none",
            }}
            onPointerDown={onPullDown}
            onPointerMove={onPullMove}
            onPointerUp={onPullEnd}
            onPointerCancel={onPullEnd}
          >
            <div className={`m-ptr ${refreshing ? "on" : ""}`} aria-hidden="true">
              <span className="spin" />{refreshing ? "Güncelleniyor…" : "Yenilemek için bırak"}
            </div>
            <Outlet />
          </div>
        </div>

        {/* ═══ ALT SABİT ÇUBUK — sahanın 5 işi ═══
            YETKİSİZ KALEM GİZLENMEZ, PASİFLEŞİR (kullanıcı kararı). Gizlemek, çubuğun
            düzenini kullanıcıdan kullanıcıya kaydırırdı: aynı ekranda aynı parmak
            hareketi farklı sayfaya giderdi — vardiyada pahalı bir yanılma. Kalem yerinde
            durur, soluk ve tıklanamaz olur; sebebi de dokunmatik ipucunda yazar. */}
        <nav className="m-tabbar" aria-label="Hızlı erişim">
          {MOBILE_TABBAR.map((item) => {
            const yetkili = canSee(item.pageKey);
            const aktif = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            const cls = `m-tab${item.center ? " mid" : ""}${aktif ? " act" : ""}`;
            const ic = <span className="m-tab-ic"><Icon name={item.icon} size={item.center ? 24 : 20} /></span>;
            if (!yetkili) {
              return (
                <button key={item.to} type="button" className={`${cls} off`} disabled
                  aria-label={`${item.label} — yetkiniz yok`} title="Bu sayfa için yetkiniz yok">
                  {ic}<span className="m-tab-lbl">{item.short}</span>
                </button>
              );
            }
            return (
              <NavLink key={item.to} to={item.to} className={cls} aria-current={aktif ? "page" : undefined}>
                {ic}<span className="m-tab-lbl">{item.short}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className={`m-scrim ${mobileNavOpen ? "open" : ""}`} onClick={() => setMobileNavOpen(false)}></div>
        <nav className={`m-drawer ${mobileNavOpen ? "open" : ""}`} aria-label="Ana menü">
          <div className="m-duser">
            <span className="m-avatar">{initials}</span>
            <div><b>{fullName}</b>{user?.email ? <span>{user.email}</span> : null}</div>
          </div>
          <div className="m-dnav">
            {facilities.length > 1 ? (
              <>
                <div className="m-dgroup">Aktif Tesis</div>
                <div className="m-ditem" style={{ cursor: "default", gap: 10 }}>
                  <Icon name="mapPin" size={18} />
                  <select aria-label="Aktif tesis" value={activeFacilityId ?? ""} onChange={(e) => switchFacility(e.target.value)}
                    style={{ flex: 1, height: 36, border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--white)", color: "var(--neutral-800)", fontSize: 14, fontWeight: 500, padding: "0 8px" }}>
                    {isSuper ? <option value="">Tüm Tesisler</option> : null}
                    {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="m-dsep" />
              </>
            ) : null}
            {visibleNav.map((group) => (
              <div key={group.label}>
                <div className="m-dgroup">{group.label}</div>
                {group.items.map((item) => (
                  <NavLink key={item.to} to={item.to} className={({ isActive }) => `m-ditem ${isActive ? "act" : ""}`}>
                    <Icon name={item.icon} size={18} />{item.label}
                  </NavLink>
                ))}
              </div>
            ))}
            {!pwa.installed ? (
              <>
                <div className="m-dsep" />
                <button type="button" className="m-ditem" style={{ color: "var(--primary)", fontWeight: 600 }} onClick={handleInstall}>
                  <Icon name="download" size={18} />Uygulamayı Yükle
                </button>
              </>
            ) : null}
            <div className="m-dsep" />
            <button type="button" className="m-ditem" style={{ color: "var(--danger)" }} disabled={logoutMutation.isPending}
              onClick={() => { setMobileNavOpen(false); logoutMutation.mutate(); }}>
              <Icon name="logout" size={18} />{logoutMutation.isPending ? "Çıkış yapılıyor…" : "Oturumu Kapat"}
            </button>
          </div>
          <div className="m-dfoot">STEPERP Saha · Palet B</div>
        </nav>

        {scannerOpen ? <ScannerOverlay onClose={() => setScannerOpen(false)} /> : null}

        {installHelp}
      </div>
    );
  }

  return (
    <div className={`app-shell${collapsed ? " sidebar-collapsed" : ""}`}>
      <header className="shell-topbar">
        <Link to="/dashboard" className="logo">
          <span className="mark" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="3" y="14" width="5" height="7" rx="1" />
              <rect x="9.5" y="9" width="5" height="12" rx="1" opacity="0.7" />
              <rect x="16" y="4" width="5" height="17" rx="1" opacity="0.4" />
            </svg>
          </span>
          <span>
            <span className="name">STEPERP</span>
            <span className="sub">Panel</span>
          </span>
        </Link>
        <button type="button" className="topbar-search" aria-label="Global arama (yakında)" title="Global arama — yakında">
          <Icon name="search" size={16} />
          <span className="ph">Lot, sipariş, ürün ara…</span>
          <kbd>Ctrl K</kbd>
        </button>
        <div className="spacer" />
        <div className="topbar-actions">
          {facilities.length > 1 ? (
            <select
              aria-label="Aktif tesis"
              title="Aktif tesis — verileri ve firma künyesini bu tesise göre gösterir"
              value={activeFacilityId ?? ""}
              onChange={(e) => switchFacility(e.target.value)}
              style={{ height: 32, borderRadius: "var(--radius-md)", border: "1px solid var(--neutral-300)", background: "var(--white)", color: "var(--neutral-800)", fontSize: 12.5, fontWeight: 600, padding: "0 8px", maxWidth: 200, cursor: "pointer" }}
            >
              {isSuper ? <option value="">Tüm Tesisler</option> : null}
              {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          ) : null}
          <NotificationBell />
          <div className="user-wrap">
            <button
              type="button"
              className="user-chip"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="avatar">{initials}</span>
              <span className="user-name">{fullName}</span>
              <Icon name="chevDown" size={15} className={menuOpen ? "chev open" : "chev"} />
            </button>
            {menuOpen ? (
              <>
                <button type="button" className="user-backdrop" aria-label="Menüyü kapat" onClick={() => setMenuOpen(false)} />
                <div className="user-menu" role="menu" onKeyDown={(e) => { if (e.key === "Escape") setMenuOpen(false); }}>
                  <div className="user-menu-head">
                    <span className="avatar lg">{initials}</span>
                    <div className="info">
                      <span className="nm">{fullName}</span>
                      {user?.email ? <span className="em">{user.email}</span> : null}
                    </div>
                  </div>
                  <div className="user-menu-sep" />
                  {!pwa.installed ? (
                    <button type="button" className="user-menu-item" role="menuitem" onClick={handleInstall} style={{ color: "var(--primary)", fontWeight: 600 }}>
                      <Icon name="download" size={16} />
                      Uygulamayı Yükle
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="user-menu-item danger"
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); logoutMutation.mutate(); }}
                    disabled={logoutMutation.isPending}
                  >
                    <Icon name="logout" size={16} />
                    {logoutMutation.isPending ? "Çıkış yapılıyor…" : "Oturumu Kapat"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <aside className="shell-sidebar" aria-label="Ana navigasyon">
        <button
          type="button"
          className="nav-collapse-btn"
          onClick={toggleSidebar}
          aria-label={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
          title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
        >
          <Icon name="chevLeft" size={16} className="nav-collapse-ic" />
        </button>
        {visibleNav.map((group) => (
          <div key={group.label}>
            <div className="nav-group">{group.label}</div>
            {group.items.map((item) =>
              item.disabled ? (
                <button
                  key={item.label}
                  type="button"
                  className="nav-item disabled"
                  aria-disabled="true"
                  title={`${item.label} — ${item.phase} fazında gelecek`}
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={onNavEnter(item.label)}
                  onMouseLeave={onNavLeave}
                >
                  <Icon name={item.icon} className="ic" />
                  <span>{item.label}</span>
                  {item.phase ? <span className="phase-pill">{item.phase}</span> : null}
                </button>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  onMouseEnter={onNavEnter(item.label)}
                  onMouseLeave={onNavLeave}
                >
                  <Icon name={item.icon} className="ic" />
                  <span>{item.label}</span>
                </NavLink>
              )
            )}
          </div>
        ))}
      </aside>

      {collapsed && navTip ? (
        <div className="nav-tooltip" style={{ top: navTip.top }} role="tooltip">{navTip.label}</div>
      ) : null}

      {/* Sekme çubuğu — mobil kabukta YOK (küçük ekranda şerit yer yemekten başka işe
          yaramaz ve bellek sınırı daha düşüktür; kullanıcı kararı). */}
      <TabBar />

      {/* Sekme gövdeleri — açık her sekme mount kalır, yalnız aktif olan görünür. */}
      <TabViews />

      {installHelp}
    </div>
  );
}
